import {
 useState, useEffect
} from 'react';
import {
 Input
} from 'shared/components/ui/input';
import {
 Button
} from 'shared/components/ui/button';
import {
 Label
} from 'shared/components/ui/label';
import {
 Plus, X
} from 'lucide-react';
type KeyValuePair = { key: string; value: string };

/**
 * Parses a JSON-stringified object into key-value pairs.
 * Returns empty array if the string is empty or invalid.
 */
function parseJsonToPairs(jsonStr: string): KeyValuePair[] {
    if (!jsonStr || !jsonStr.trim()) return [];
    try {
        const obj = JSON.parse(jsonStr);
        if (typeof obj === 'object' && obj !== null && !Array.isArray(obj)) {
            return Object.entries(obj).map(([key, value]) => ({
                key,
                value: String(value),
            }));
        }
    } catch {
        // Not valid JSON — return empty
    }
    return [];
}

/**
 * Serializes key-value pairs into a JSON string.
 * Returns empty string if no pairs with non-empty keys exist.
 */
function pairsToJson(pairs: KeyValuePair[]): string {
    const validPairs = pairs.filter(p => p.key.trim());
    if (validPairs.length === 0) return '';
    const obj: Record<string, string> = {};
    for (const pair of validPairs) {
        obj[pair.key.trim()] = pair.value;
    }
    return JSON.stringify(obj);
}

const KeyValueEditor = ({
    label,
    value,
    onChange,
    placeholder,
}: {
    label?: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
}) => {
    const [pairs, setPairs] = useState<KeyValuePair[]>(() => {
        const parsed = parseJsonToPairs(value);
        return parsed.length > 0 ? parsed : [];
    });

    // Sync from external value changes (e.g. loading from server)
    useEffect(() => {
        const parsed = parseJsonToPairs(value);
        if (parsed.length > 0) {
            setPairs(parsed);
        } else if (!value || !value.trim()) {
            setPairs([]);
        }
    }, [value]);

    const emitChange = (updatedPairs: KeyValuePair[]) => {
        setPairs(updatedPairs);
        onChange(pairsToJson(updatedPairs));
    };

    const handleAdd = () => {
        emitChange([...pairs, {
 key: '',
value: ''
}]);
    };

    const handleRemove = (index: number) => {
        emitChange(pairs.filter((_, i) => i !== index));
    };

    const handleKeyChange = (index: number, newKey: string) => {
        const updated = pairs.map((p, i) => i === index ? {
 ...p,
key: newKey
} : p);
        emitChange(updated);
    };

    const handleValueChange = (index: number, newValue: string) => {
        const updated = pairs.map((p, i) => i === index ? {
 ...p,
value: newValue
} : p);
        emitChange(updated);
    };

    return (
        <div className="space-y-2">
            {label && <Label>{label}</Label>}

            {pairs.length > 0 && (
                <div className="space-y-2">
                    {pairs.map((pair, index) => (
                        <div key={index} className="flex items-start gap-2">
                            <Input
                                value={pair.key}
                                onChange={(e) => handleKeyChange(index, e.target.value)}
                                placeholder="Key"
                                className="w-[35%] shrink-0"
                            />
                            <span className="text-muted-foreground text-sm mt-2">=</span>
                            <Input
                                value={pair.value}
                                onChange={(e) => handleValueChange(index, e.target.value)}
                                placeholder="Value"
                                className="flex-1"
                            />
                            <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 shrink-0 mt-0.5 text-destructive hover:text-destructive"
                                onClick={() => handleRemove(index)}
                            >
                                <X className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleAdd}
                className="w-full"
            >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                {placeholder || 'Add pair'}
            </Button>
        </div>
    );
};

export default KeyValueEditor;
